<template>
  <v-dialog max-width="1200" :model-value="dialog">
    <v-card>
      <v-toolbar color="#ffb500" dark>
        <v-toolbar-title class="text-white">
          Répartitions commandes en points
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon :icon="mdiClose"></v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <div v-if="gimCustomsDistribution && gimCustomsDistribution.length > 0">
          <v-text-field
            v-model="search"
            :prepend-inner-icon="mdiMagnify"
            label="Rechercher un type"
            single-line
            hide-details
            variant="outlined"
            density="compact"
            class="mb-4"
          ></v-text-field>

          <!-- Boucle pour chaque année -->
          <v-card
            v-for="yearData in filteredDistribution"
            :key="yearData.year"
            elevation="2"
            rounded="lg"
            class="mb-6"
          >
            <v-card-title class="d-flex align-center bg-primary-lighten-5 pa-4">
              <v-icon
                :icon="mdiCalendarMonth"
                size="large"
                color="#ffb500"
                class="mr-3"
              ></v-icon>
              <h2 class="text-h5 mr-4">{{ yearData.year }}</h2>
              <v-chip color="#32857e" class="text-black mr-2">
                {{ yearData.totalOrders }} commandes
              </v-chip>
              <v-chip color="#b26f04" class="text-black">
                {{ yearData.totalProduct }} produits
              </v-chip>
              <v-chip color="#b26f04" class="text-black ml-2">
                {{ yearData.totalProductDistinct }} références
              </v-chip>
              <v-spacer></v-spacer>
              <span class="font-weight-bold text-subtitle-1">
                {{
                  Math.ceil(calculateTotalValue(yearData)).toLocaleString(
                    "fr-FR",
                  )
                }}
                €
              </span>
            </v-card-title>

            <v-card-text class="pa-4">
              <!-- Distribution par chips -->
              <div class="d-flex flex-wrap gap-2 mb-4">
                <v-chip
                  v-for="item in yearData.distribution"
                  :key="`${yearData.year}-${item.gimCustom.id}`"
                  :color="getGimCustomColor(item.gimCustom.id)"
                  class="ma-1"
                  size="large"
                  label
                  :style="{
                    opacity: isItemFiltered(item) ? 1 : 0.5,
                  }"
                >
                  <v-icon start :icon="mdiShapeOutline" size="small"></v-icon>
                  {{ item.gimCustom.name }}:
                  {{ item.percentageValue.toFixed(1) }}%
                </v-chip>
              </div>

              <!-- Barre de distribution -->
              <div
                class="distribution-chart mb-4"
                style="
                  height: 40px;
                  display: flex;
                  width: 100%;
                  border-radius: 8px;
                  overflow: hidden;
                "
              >
                <div
                  v-for="item in yearData.distribution"
                  :key="`${yearData.year}-bar-${item.gimCustom.id}`"
                  :style="{
                    width: `${item.percentageValue}%`,
                    backgroundColor: getGimCustomColor(item.gimCustom.id),
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    minWidth: '20px',
                    transition: 'width 0.5s ease',
                    filter: isItemFiltered(item) ? 'none' : 'grayscale(70%)',
                  }"
                  :title="`${
                    item.gimCustom.name
                  }: ${item.percentageValue.toFixed(2)}% - ${
                    item.productCount
                  } produits`"
                >
                  {{
                    item.percentageValue > 8
                      ? item.gimCustom.name + " " + item.percentageValue + "%"
                      : ""
                  }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
        <div v-else class="d-flex flex-column align-center justify-center my-8">
          <v-icon
            :icon="mdiInformationOutline"
            size="x-large"
            color="grey"
            class="mb-4"
          ></v-icon>
          <p class="text-subtitle-1">
            Aucune donnée de distribution disponible
          </p>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          prepend-icon="$close"
          variant="elevated"
          color="#ffb500"
          @click="$emit('close')"
          rounded="lg"
          class="text-white"
        >
          Fermer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import {
  mdiMagnify,
  mdiCalendarMonth,
  mdiShapeOutline,
  mdiClose,
  mdiInformationOutline,
  mdiChartBar,
  mdiCurrencyEur,
} from "@mdi/js";

export default {
  name: "OrderOneStrykerStatsDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mdiMagnify,
      mdiCalendarMonth,
      mdiShapeOutline,
      mdiClose,
      mdiInformationOutline,
      mdiChartBar,
      mdiCurrencyEur,
      search: "",
    };
  },
  computed: {
    ...mapState({
      gimCustomsDistribution: (state) => state.kpi.gimCustomsDistributionItems,
    }),

    // Filtre les données en fonction de la recherche
    filteredDistribution() {
      if (!this.gimCustomsDistribution || !this.gimCustomsDistribution.length) {
        return [];
      }

      // Si pas de recherche, retourner toutes les données
      if (!this.search) {
        return this.gimCustomsDistribution;
      }

      // Cloner les données pour ne pas modifier l'original
      return this.gimCustomsDistribution
        .map((yearData) => {
          // Copier l'année et le total des commandes
          const filtered = {
            ...yearData,
            // Filtrer la distribution
            distribution: yearData.distribution.filter((item) =>
              item.gimCustom.name
                .toLowerCase()
                .includes(this.search.toLowerCase()),
            ),
          };
          return filtered;
        })
        .filter((yearData) => yearData.distribution.length > 0); // Ne garder que les années avec des résultats
    },
  },

  methods: {
    close() {
      this.$emit("close");
    },

    calculateTotalProducts(yearData) {
      if (!yearData || !yearData.distribution) return 0;
      return yearData.distribution.reduce(
        (sum, item) => sum + item.productCount,
        0,
      );
    },

    calculateTotalValue(yearData) {
      if (!yearData || !yearData.distribution) return 0;
      return yearData.distribution.reduce(
        (sum, item) => sum + item.totalValue,
        0,
      );
    },

    isItemFiltered(item) {
      if (!this.search) return true;
      return item.gimCustom.name
        .toLowerCase()
        .includes(this.search.toLowerCase());
    },

    getGimCustomColor(id) {
      // Couleurs Stryker adaptées pour chaque type de GIM Custom
      const colors = {
        1: "#32857e", // SERVICE
        2: "#ffb500", // CAPITAL
        3: "#b26f04", // IMPLANT
        4: "#2b6f6a", // CONSOMMABLE
        5: "#f0c781", // FAUX
      };

      return colors[id] || "#909399"; // Couleur par défaut pour les types inconnus
    },
  },
};
</script>
